import React, { useState } from 'react';
import { Button, FilterSection, AdvancedFilters, Modal } from "@/components/ui";
import { Upload, User, Download, UserPlus, Info } from "lucide-react";
import { downloadCustomerTemplate } from "../utils/templateUtils";
import { VIEW_TABS, SORT_OPTIONS } from "@/constants/globalConstants";

export const CustomersControlPanel = ({
  search, setSearch,
  filters, setFilters,
  sortBy, setSortBy,
  showAdvancedFilters, setShowAdvancedFilters,
  filterOptions,
  view, setView,
  viewStyle, setViewStyle,
  onAddClick, onBulkClick,
  stages,
  onStartAllocation,
  totalUnassigned,
  onExport,
  isExporting,
  user,
  activityStats,
  leads = []
}) => {
  const isAdmin = ["admin", "owner", "Verve-admin", "systemadmin"].includes(user?.role?.toLowerCase());
  const [showAppModal, setShowAppModal] = useState(false);

  // Filter and sort appointments
  const scheduledApps = leads.filter(l => l.appointmentDate).sort((a, b) => new Date(b.appointmentDate) - new Date(a.appointmentDate));

  const handleAppClick = (leadId) => {
    setShowAppModal(false);
    // Add ?highlight=id to the URL and reload/trigger state
    const url = new URL(window.location);
    url.searchParams.set('highlight', leadId);
    window.history.pushState({}, '', url);
    window.dispatchEvent(new Event('popstate'));
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Top Section: Navigation & Main Actions */}
      <div className="flex flex-col md:flex-row items-center justify-between border-b border-slate-200 px-1 gap-4">
        {isAdmin && (
          <div className="flex items-center overflow-x-auto no-scrollbar">
            {VIEW_TABS.map(tab => (
              <button
                key={tab.id}
                onClick={() => setView(tab.id)}
                className={`flex items-center gap-2 px-6 py-3 text-[13px] font-medium transition-all relative whitespace-nowrap ${view === tab.id
                  ? "text-brand-blue border-b-2 border-brand-blue bg-blue-50/30"
                  : "text-slate-500 hover:text-slate-700 hover:bg-slate-50"
                  }`}
              >
                {tab.id === "uploads" ? <Upload size={14} /> : <User size={14} />}
                {tab.label}
              </button>
            ))}
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2 pb-4 md:pb-0 w-full md:w-auto justify-center md:justify-end md:ml-auto">
          {/* Developer: Nithin | Date: 09-07-2026 | Changes: Allocation button commented out — not required */}
          {/* {view === "assign" && isAdmin && (
            <Button
              onClick={onStartAllocation}
              disabled={totalUnassigned === 0}
              variant="orange"
              size="sm"
              className="px-6 py-2 min-w-[140px] hover:scale-105 active:scale-95 transition-all"
              leftIcon={<UserPlus size={16} />}
            >
              Allocation
            </Button>
          )} */}
          {/*developer: Nithin
    date : 2024-06-19
    changes : Staff can downolad the form  */}
          <>
            {/*               <button
                onClick={() => setShowAppModal(true)}
                className="btn-secondary flex items-center justify-center gap-2 py-2 px-4 rounded-md border border-brand-blue text-brand-blue hover:bg-blue-50 transition-colors font-medium text-[13px] bg-white whitespace-nowrap min-w-[140px]"
                title="View Scheduled Appointments"
              >
                <Info size={16} /> App Scheduled
              </button> */}

            <Button
              onClick={downloadCustomerTemplate}
              variant="secondary"
              size="sm"
              className="whitespace-nowrap min-w-[140px]"
              leftIcon={<Download size={16} />}
            >
              Download Template
            </Button>

            <label className="btn-secondary flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer py-2 px-4 text-[13px] transition-colors border border-slate-200 rounded-md min-w-[140px] hover:bg-slate-100">
              <input
                type="file"
                className="hidden"
                accept=".csv, .xlsx, .xls"
                onChange={(e) => {
                  if (e.target.files && e.target.files[0]) {
                    onBulkClick(e.target.files[0]);
                    e.target.value = null;
                  }
                }}
              />
              <Upload size={16} /> Upload Contacts
            </label>
          </>
        </div>
      </div>

      {/* Control Panel Section */}
      {view === "assign" && (
        <FilterSection
          search={search}
          setSearch={setSearch}
          filters={filters}
          setFilters={setFilters}
          viewStyle={viewStyle}
          setViewStyle={setViewStyle}
          onAddClick={onAddClick}
          addLabel="Add Contact"
          onExport={isAdmin ? onExport : undefined}
          isExporting={isExporting}
          isAdvancedFilterInline={true}
          showAdvancedFilters={showAdvancedFilters}
          setShowAdvancedFilters={setShowAdvancedFilters}
          hideFilterButton={true}
          hideViewToggle={true}
          statusOptions={(isAdmin ? stages : stages?.filter(s => s.key !== "new"))?.map(s => ({ value: s.key, label: s.label }))}
          statusPlaceholder="All Contact Stages"
          AdvancedFiltersComponent={({ isInline }) => (
            <AdvancedFilters  //nithin
              filters={filters}
              onFilterChange={setFilters}
              sortBy={sortBy}
              onSortChange={setSortBy}
              sortOptions={SORT_OPTIONS}
              statusOptions={stages?.map(s => ({ value: s.key, label: s.label }))}
              createdCount={filters.activityStatus === 'all' || !filters.activityStatus ? (activityStats?.created || 0) : (activityStats?.assigned || 0)}
              countLabel={filters.activityStatus === 'all' || !filters.activityStatus
                ? "Created"
                : (stages?.find(s => s.key === filters.activityStatus)?.label || "Total")}
              onReset={() => {
                setFilters({
                  activityStatus: 'all',
                  date: 'all',
                  fromDate: '',
                  toDate: ''
                });
              }}
              statusKey="activityStatus"
              isInline={isInline}
            />
          )}
        />
      )}

      {showAppModal && (
        <Modal
          isOpen={showAppModal}
          onClose={() => setShowAppModal(false)}
          title="Scheduled Appointments"
          subtitle="List of all upcoming and past site visits."
          size="lg"
        >
          <div className="flex flex-col gap-2 max-h-[60vh] overflow-y-auto no-scrollbar pb-4">
            {scheduledApps.length === 0 ? (
              <p className="text-sm text-slate-500 text-center py-4">No appointments scheduled.</p>
            ) : (
              scheduledApps.map(app => {
                const appDate = new Date(app.appointmentDate);
                const today = new Date();
                today.setHours(0, 0, 0, 0);
                const isPast = appDate < today;

                return (
                  <div
                    key={app._id}
                    onClick={() => handleAppClick(app._id)}
                    className={`flex items-center justify-between p-3 border rounded-lg cursor-pointer transition-colors ${isPast ? 'bg-slate-50 border-slate-100 opacity-60 hover:opacity-80' : 'bg-white border-slate-200 hover:border-brand-blue hover:shadow-sm'}`}
                  >
                    <div className="flex flex-col">
                      <span className={`text-sm font-medium ${isPast ? 'text-slate-500' : 'text-slate-800'}`}>
                        Customer: {app.customerName || `${app.firstName || ''} ${app.lastName || ''}`.trim() || 'Unknown'}
                      </span>
                      <span className="text-xs text-slate-400 mt-1">
                        Assigned Site Exec: {app.siteExecutive?.name || 'Unassigned'}
                      </span>
                    </div>
                    <div className={`text-xs font-medium px-2 py-1 rounded-full ${isPast ? 'bg-slate-200 text-slate-600' : 'bg-blue-50 text-brand-blue'}`}>
                      {appDate.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </div>
                  </div>
                );
              })
            )}
          </div>
        </Modal>
      )}
    </div>
  );
};
